"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (Object.hasOwnProperty.call(mod, k)) result[k] = mod[k];
    result["default"] = mod;
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
var sjcl_1 = __importDefault(require("sjcl"));
var node_rsa_1 = __importDefault(require("node-rsa"));
var Constants = __importStar(require("./Constants"));
sjcl_1.default.beware['CBC mode is dangerous because it doesn\'t protect message integrity.']();
exports.HMAC_SIZE_BYTES = 32;
var AsymmetricKeyPair = /** @class */ (function () {
    function AsymmetricKeyPair(publicKey, privateKey) {
        this.publicKey = publicKey;
        this.privateKey = privateKey;
    }
    return AsymmetricKeyPair;
}());
exports.AsymmetricKeyPair = AsymmetricKeyPair;
function deriveKey(salt, password) {
    var keySize = 190 * 8;
    return sjcl_1.default.codec.base64.fromBits(sjcl_1.default.misc.scrypt(password, salt, 16384, 8, 1, keySize));
}
exports.deriveKey = deriveKey;
function genUid() {
    var rand = sjcl_1.default.random.randomWords(4);
    return sjcl_1.default.codec.hex.fromBits(hmac256(rand, rand));
}
exports.genUid = genUid;
function hmac256(salt, key) {
    var hmac = new sjcl_1.default.misc.hmac(salt);
    return hmac.encrypt(key);
}
var CryptoManager = /** @class */ (function () {
    function CryptoManager(_keyBase64, salt, version) {
        if (version === void 0) { version = Constants.CURRENT_VERSION; }
        this.cipherWords = 4;
        this.version = version;
        var key = sjcl_1.default.codec.base64.toBits(_keyBase64);
        // FIXME: Clean up all exeptions
        if (version > Constants.CURRENT_VERSION) {
            throw new Error('VersionTooNewException');
        }
        else if (version === 1) {
            this.key = key;
        }
        else {
            this.key = hmac256(sjcl_1.default.codec.utf8String.toBits(salt), key);
        }
        this._updateDerivedKeys();
    }
    CryptoManager.fromDerivedKey = function (key, version) {
        if (version === void 0) { version = Constants.CURRENT_VERSION; }
        // FIXME: Cleanup this hack
        var ret = new CryptoManager('', '', version);
        ret.key = sjcl_1.default.codec.bytes.toBits(key);
        ret._updateDerivedKeys();
        return ret;
    };
    CryptoManager.prototype._updateDerivedKeys = function () {
        this.cipherKey = hmac256(sjcl_1.default.codec.utf8String.toBits('aes'), this.key);
        this.hmacKey = hmac256(sjcl_1.default.codec.utf8String.toBits('hmac'), this.key);
    };
    CryptoManager.prototype.encryptBits = function (content) {
        var iv = sjcl_1.default.random.randomWords(this.cipherWords);
        var prp = new sjcl_1.default.cipher.aes(this.cipherKey);
        var cipherText = sjcl_1.default.mode.cbc.encrypt(prp, content, iv);
        return sjcl_1.default.codec.bytes.fromBits(iv.concat(cipherText));
    };
    CryptoManager.prototype.decryptBits = function (content) {
        var cipherText = sjcl_1.default.codec.bytes.toBits(content);
        var iv = cipherText.splice(0, this.cipherWords);
        var prp = new sjcl_1.default.cipher.aes(this.cipherKey);
        var clearText = sjcl_1.default.mode.cbc.decrypt(prp, cipherText, iv);
        return clearText;
    };
    CryptoManager.prototype.encryptBytes = function (content) {
        return this.encryptBits(sjcl_1.default.codec.bytes.toBits(content));
    };
    CryptoManager.prototype.decryptBytes = function (content) {
        return sjcl_1.default.codec.bytes.fromBits(this.decryptBits(content));
    };
    CryptoManager.prototype.encrypt = function (content) {
        return this.encryptBits(sjcl_1.default.codec.utf8String.toBits(content));
    };
    CryptoManager.prototype.decrypt = function (content) {
        return sjcl_1.default.codec.utf8String.fromBits(this.decryptBits(content));
    };
    CryptoManager.prototype.getEncryptedKey = function (keyPair, publicKey) {
        var cryptoManager = new AsymmetricCryptoManager(keyPair);
        return cryptoManager.encryptBytes(publicKey, sjcl_1.default.codec.bytes.fromBits(this.key));
    };
    CryptoManager.prototype.hmac = function (content) {
        return sjcl_1.default.codec.bytes.fromBits(this.hmacBase(content));
    };
    CryptoManager.prototype.hmac64 = function (content) {
        return sjcl_1.default.codec.base64.fromBits(this.hmacBase(content));
    };
    CryptoManager.prototype.hmacHex = function (content) {
        return sjcl_1.default.codec.hex.fromBits(this.hmacBase(content));
    };
    CryptoManager.prototype.hmacBase = function (_content) {
        var content;
        if (this.version === 1) {
            content = sjcl_1.default.codec.bytes.toBits(_content);
        }
        else {
            content = sjcl_1.default.codec.bytes.toBits(_content.concat([this.version]));
        }
        return hmac256(this.hmacKey, content);
    };
    return CryptoManager;
}());
exports.CryptoManager = CryptoManager;
function bufferToArray(buffer) {
    return Array.prototype.slice.call(buffer);
}
var AsymmetricCryptoManager = /** @class */ (function () {
    function AsymmetricCryptoManager(keyPair) {
        this.keyPair = new node_rsa_1.default();
        this.keyPair.importKey(Buffer.from(keyPair.privateKey), 'pkcs8-der');
    }
    AsymmetricCryptoManager.generateKeyPair = function () {
        var keyPair = new node_rsa_1.default();
        keyPair.generateKeyPair(3072, 65537);
        var pubkey = keyPair.exportKey('pkcs8-public-der');
        var privkey = keyPair.exportKey('pkcs8-private-der');
        return new AsymmetricKeyPair(bufferToArray(pubkey), bufferToArray(privkey));
    };
    AsymmetricCryptoManager.prototype.encryptBytes = function (publicKey, content) {
        var key = new node_rsa_1.default();
        key.importKey(Buffer.from(publicKey), 'pkcs8-public-der');
        return bufferToArray(key.encrypt(Buffer.from(content), 'buffer'));
    };
    AsymmetricCryptoManager.prototype.decryptBytes = function (content) {
        return bufferToArray(this.keyPair.decrypt(Buffer.from(content), 'buffer'));
    };
    return AsymmetricCryptoManager;
}());
exports.AsymmetricCryptoManager = AsymmetricCryptoManager;
//# sourceMappingURL=Crypto.js.map